import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import NavBarButton from '../NavBarButton/NavBarButton'
import { useAppSelector } from 'hooks/reduxHooks'
import { getNumberOfActiveUserCartItems, getUserCartUpdatedAt } from 'selectors/userCart'
import useToggle from 'hooks/useToggle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { matchPath } from 'react-router'
import UserCartDropdownPreview from 'components/Pages/UserCartPage/UserCartPreview/UserCartDropdownPreview'
import debounce from 'lodash/debounce'
import {
  isCartAutoPreviewDropdownSuppressed,
  suppressCartAutoDropdown,
} from 'components/Pages/UserCartPage/UserCartPreview/cartAutoDropdownCookie'
import config from 'constants/config'
import { dateDifference } from 'lib/datetime/dateUtils'
import { USER_CART_PREVIEW_MAX_DAYS_TO_SHOW } from 'constants/userCart'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import Badge from 'LuxKit/Badge'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'

interface Props {
  className?: string
  allowCartPreview?: boolean
}

function NavBarUserCartButton(props: Props) {
  const {
    allowCartPreview = true,
  } = props

  const numberOfActiveItems = useAppSelector(getNumberOfActiveUserCartItems)
  const userCartUpdatedAt = useAppSelector(getUserCartUpdatedAt)
  const currentPath = useAppSelector(state => state.router.location.pathname)
  const isCartPage = useMemo(() => !!matchPath(currentPath, { path: '/:regionCode/cart', exact: true }), [currentPath])
  const isAgentHub = config.agentHub.isEnabled
  const isMobile = useIsMobileDevice()
  const wasUpdatedRecently = !userCartUpdatedAt || dateDifference(new Date(), userCartUpdatedAt).days < USER_CART_PREVIEW_MAX_DAYS_TO_SHOW
  const canAutoOpen = numberOfActiveItems > 0 && !isAgentHub && wasUpdatedRecently && !isMobile

  const buttonRef = useRef<HTMLButtonElement>(null)
  const [isHovered, , openHovered, closeHovered] = useToggle()
  const [persistentOpen, setPersistentOpen] = useState(false)
  const [isAutoOpened, setIsAutoOpened] = useState(false)

  const canShowCartPreview = numberOfActiveItems > 0 && !isCartPage && allowCartPreview
  const showCartPreview = (isHovered || persistentOpen) && canShowCartPreview

  const debouncedCloseHovered = useMemo(() => debounce(closeHovered, 500), [closeHovered])

  const handleMouseEnter = useCallback(() => {
    if (canShowCartPreview) {
      debouncedCloseHovered.cancel()
      openHovered()
    }
  }, [canShowCartPreview, debouncedCloseHovered, openHovered])

  const handleClose = useCallback(() => {
    setPersistentOpen(false)
    closeHovered()

    if (isAutoOpened) {
      setIsAutoOpened(false)
    }
  }, [closeHovered, isAutoOpened])

  // Handle auto-opening logic
  useEffect(() => {
    if (canAutoOpen && !isCartAutoPreviewDropdownSuppressed()) {
      setPersistentOpen(true)
      setIsAutoOpened(true)
      suppressCartAutoDropdown()
    }
  }, [canAutoOpen])

  // Auto close the cart preview when the user navigates to a different page
  useEffect(() => {
    setPersistentOpen(false)
    setIsAutoOpened(false)
  }, [currentPath])

  return (<div
    onMouseEnter={handleMouseEnter}
    onMouseLeave={!isAutoOpened ? debouncedCloseHovered : undefined}
  >
    <NavBarButton
      ref={buttonRef}
      className={props.className}
      data-testid="cart-button"
      to="/cart"
      onClick={handleClose}
      icon={<LineShoppingCartIcon />}
      badge={!!numberOfActiveItems && <Badge mode="critical" kind="string">
        {numberOfActiveItems}
      </Badge>}
    />
    <CSSBreakpoint min="desktop">
      <UserCartDropdownPreview
        open={showCartPreview}
        onClose={handleClose}
        buttonRef={buttonRef}
        hasDismissButton={isAutoOpened}
      />
    </CSSBreakpoint>
  </div>)
}

export default React.memo(NavBarUserCartButton)
